import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useApi } from "./AuthContext";
import ProposalPreview from "./ProposalPreview";
import CurrencyInput from "./CurrencyInput";
import { formatUSD } from "../utils/currency";
import {
  DollarSign, Plus, Pencil, Trash2, X, Check, FileText, Download,
  Eye, ArrowLeft, Send, CheckCircle, History, Clock
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-amber-50 text-amber-800 border-amber-200",
  sent: "bg-blue-50 text-blue-800 border-blue-200",
  approved: "bg-green-50 text-green-800 border-green-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};
const STATUS_LABELS = {
  draft: "Awaiting Submission",
  sent: "Sent",
  approved: "Approved",
  rejected: "Rejected",
};

const COMPANY_BIO = "Georgia Outdoor Worx delivers high-end craftsmanship and professional expertise to your lawn and garden. We are fully licensed and insured, providing the peace of mind and premium results your home deserves.";

const emptyForm = () => ({
  client_id: "", client_name_manual: "", title: "", description: COMPANY_BIO, notes: "",
  items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
  property_address: "", project_description: "", company_name: "",
  proposal_services: [{ title: "", bullets: [""] }],
  timeline: "", payment_terms: "To be discussed upon approval.",
  scheduled_date: "",
  prepared_by_name: "Felipe Lima", prepared_by_phone: "478.418.2629",
});

// Format ISO date as "MMM DD, YYYY · h:mm AM/PM"
function formatHistoryDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { year: "numeric", month: "short", day: "2-digit", hour: "numeric", minute: "2-digit", hour12: true });
  } catch (e) { return iso; }
}

// ====================== MAIN MODULE ======================
export default function BudgetsModule({ readOnly = false, onComposeEmail = null }) {
  const api = useApi();
  const [budgets, setBudgets] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [view, setView] = useState("list"); // list | editor
  const [editingBudget, setEditingBudget] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const previewRef = useRef(null);

  const fetchBudgets = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/budgets?status=${filterStatus}`);
      setBudgets(data);
    } catch (err) { toast.error("Failed to load proposals"); }
    finally { setLoading(false); }
  }, [api, filterStatus]);

  const fetchClients = useCallback(async () => {
    try { const { data } = await api.get("/admin/clients"); setClients(data); }
    catch (err) { console.warn("Failed to load clients:", err?.message); }
  }, [api]);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  const clientName = useMemo(() => 
    form.client_name_manual || clients.find(c => c.id === form.client_id)?.name || editingBudget?.client_name || "",
    [clients, form.client_id, form.client_name_manual, editingBudget?.client_name]
  );

  const formTotal = useMemo(() => 
    form.items.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || 0), 0),
    [form.items]
  );

  const resetForm = () => { setForm(emptyForm()); setEditingBudget(null); setView("list"); };

  // Open the Standard editor (Layout 01) for new or existing proposals
  const openEditor = async (budget = null) => {
    if (budget) {
      // Lazy-fetch full budget detail so legacy docs get a freshly-issued public_token
      let full = budget;
      if (!budget.public_token) {
        try {
          const { data } = await api.get(`/admin/budgets/${budget.id}`);
          full = data;
        } catch (e) { /* fall back to list payload */ }
      }
      setEditingBudget(full);
      setForm({
        client_id: full.client_id, client_name_manual: full.client_name_manual || full.client_name || "",
        title: full.title || "",
        description: full.description || COMPANY_BIO, notes: full.notes || "",
        items: full.items?.length ? full.items : [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
        property_address: full.property_address || "", company_name: full.company_name || "",
        project_description: full.project_description || "",
        proposal_services: full.proposal_services?.length ? full.proposal_services : [{ title: "", bullets: [""] }],
        timeline: full.timeline || "", payment_terms: full.payment_terms || "To be discussed upon approval.",
        scheduled_date: full.scheduled_date || "", tracking_id: full.tracking_id || "",
        prepared_by_name: full.prepared_by_name || "Felipe Lima",
        prepared_by_phone: full.prepared_by_phone || "478.418.2629",
      });
    } else {
      setEditingBudget(null);
      setForm(emptyForm());
    }
    setView("editor");
  };

  // Service editor helpers
  const addService = () => setForm(f => ({ ...f, proposal_services: [...f.proposal_services, { title: "", bullets: [""] }] }));
  const removeService = (i) => setForm(f => ({ ...f, proposal_services: f.proposal_services.filter((_, idx) => idx !== i) }));
  const updateServiceTitle = (i, val) => {
    setForm(f => { const s = [...f.proposal_services]; s[i] = { ...s[i], title: val }; return { ...f, proposal_services: s }; });
  };
  const addBullet = (i) => {
    setForm(f => { const s = [...f.proposal_services]; s[i] = { ...s[i], bullets: [...s[i].bullets, ""] }; return { ...f, proposal_services: s }; });
  };
  const updateBullet = (si, bi, val) => {
    setForm(f => { const s = [...f.proposal_services]; const b = [...s[si].bullets]; b[bi] = val; s[si] = { ...s[si], bullets: b }; return { ...f, proposal_services: s }; });
  };
  const removeBullet = (si, bi) => {
    setForm(f => { const s = [...f.proposal_services]; s[si] = { ...s[si], bullets: s[si].bullets.filter((_, j) => j !== bi) }; return { ...f, proposal_services: s }; });
  };

  // Cost items
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0, total: 0 }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, val) => {
    setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [field]: field === "description" ? val : Number(val) }; return { ...f, items }; });
  };

  const handleSave = async () => {
    if (!form.title) { toast.error("Proposal title is required"); return; }
    if (!form.client_id && !form.client_name_manual?.trim()) { toast.error("Select a client or type a name"); return; }
    try {
      const payload = { ...form };
      if (!payload.client_id) payload.client_id = "manual";
      let saved;
      if (editingBudget) {
        const { data } = await api.put(`/admin/budgets/${editingBudget.id}`, payload);
        saved = data;
        toast.success("Proposal saved");
      } else {
        const { data } = await api.post("/admin/budgets", payload);
        saved = data;
        toast.success("Proposal created");
      }
      // Stay on the editor and reflect new history entries inline
      setEditingBudget(saved);
      fetchBudgets();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const handleStatusChange = async (budget, newStatus) => {
    // Only require confirmation for the irreversible "approved" transition (creates SO)
    if (newStatus === "approved" && !window.confirm("Approve this proposal? This will automatically create a Service Order.")) return;
    try {
      const { data: updated } = await api.put(`/admin/budgets/${budget.id}`, { status: newStatus });
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
      // If currently editing this same budget, refresh editingBudget so history timeline updates live
      if (editingBudget && editingBudget.id === budget.id) setEditingBudget(updated);
      fetchBudgets();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (budget) => {
    if (!window.confirm(`Delete proposal "${budget.tracking_id || budget.title}"?`)) return;
    try { await api.delete(`/admin/budgets/${budget.id}`); toast.success("Deleted"); fetchBudgets(); }
    catch (err) { toast.error("Failed to delete"); }
  };

  const handleExportPDF = async () => {
    const el = document.getElementById("proposal-preview");
    if (!el) return;
    toast.info("Generating PDF...");
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      html2pdf().set({
        margin: 0, filename: `${form.tracking_id || "proposal"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      }).from(el).save();
    } catch (err) { toast.error("PDF export failed"); }
  };

  // ======================== LIST VIEW ========================
  if (view === "list") {
    if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;
    return (
      <div data-testid="budgets-module" className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-forest-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-800">Proposals</h2>
              <p className="text-sm text-stone-500">{budgets.length} proposal{budgets.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="filter-status">
              <option value="">All Status</option>
              <option value="draft">Awaiting Submission</option>
              <option value="sent">Sent</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            {!readOnly && (
              <button data-testid="add-budget-btn" onClick={() => openEditor()}
                className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                <Plus className="w-4 h-4" /> New Proposal
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
          <table className="w-full text-sm" data-testid="budgets-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-4 py-3 font-medium text-stone-600">ID</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Client</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600">Title</th>
                <th className="text-right px-4 py-3 font-medium text-stone-600">Total</th>
                <th className="text-center px-4 py-3 font-medium text-stone-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-stone-600 min-w-[280px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {budgets.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400">No proposals found</td></tr>
              ) : budgets.map(b => (
                <tr key={b.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`budget-row-${b.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">{b.tracking_id || b.id.slice(0, 8)}</td>
                  <td className="px-4 py-3 font-medium text-stone-800">{b.client_name}</td>
                  <td className="px-4 py-3 text-stone-600">{b.title}</td>
                  <td className="px-4 py-3 text-right font-semibold text-stone-800">{formatUSD(b.total_amount || 0)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[b.status]}`}>
                      {STATUS_LABELS[b.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <button
                        data-testid={`status-draft-${b.id}`}
                        onClick={() => !readOnly && b.status !== "draft" && handleStatusChange(b, "draft")}
                        disabled={readOnly || b.status === "draft"}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          b.status === "draft"
                            ? "bg-amber-500 text-white border-amber-500 cursor-default"
                            : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"
                        }`}
                        title="Awaiting Submission"
                      >
                        <Clock className="w-3 h-3 inline mr-1" /> Awaiting
                      </button>
                      <button
                        data-testid={`status-sent-${b.id}`}
                        onClick={() => !readOnly && b.status !== "sent" && handleStatusChange(b, "sent")}
                        disabled={readOnly || b.status === "sent"}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          b.status === "sent"
                            ? "bg-blue-600 text-white border-blue-600 cursor-default"
                            : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"
                        }`}
                        title="Sent"
                      >
                        <Send className="w-3 h-3 inline mr-1" /> Sent
                      </button>
                      <button
                        data-testid={`status-approved-${b.id}`}
                        onClick={() => !readOnly && b.status !== "approved" && handleStatusChange(b, "approved")}
                        disabled={readOnly || b.status === "approved"}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          b.status === "approved"
                            ? "bg-green-600 text-white border-green-600 cursor-default"
                            : "bg-white text-green-700 border-green-200 hover:bg-green-50"
                        }`}
                        title="Approve (creates Service Order)"
                      >
                        <CheckCircle className="w-3 h-3 inline mr-1" /> Approved
                      </button>
                      <button
                        onClick={() => openEditor(b)}
                        data-testid={`edit-budget-${b.id}`}
                        className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-forest-700"
                        title="Open / Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {!readOnly && (
                        <button onClick={() => handleDelete(b)} className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ======================== EDITOR VIEW (Split Screen) ========================
  return (
    <div data-testid="proposal-editor" className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-white rounded-xl border border-stone-200 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={resetForm} className="flex items-center gap-1 text-stone-500 hover:text-stone-700 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="text-stone-300">|</span>
          <h2 className="font-semibold text-stone-800">{editingBudget ? `Edit ${editingBudget.tracking_id || "Proposal"}` : "New Proposal"}</h2>
        </div>
        <div className="flex items-center gap-2">
          {(() => {
            const linkedClient = clients.find(c => c.id === form.client_id);
            const clientEmail = linkedClient?.email;
            if (!clientEmail || !onComposeEmail) return null;
            return (
              <button
                type="button"
                data-testid="proposal-email-client-btn"
                onClick={async () => {
                  const subject = `Georgia Outdoor Worx - Proposal for ${form.property_address || form.title || "your project"}`;
                  // Build a rich HTML email body. Include the public link as a CTA only if the proposal is saved (has a token).
                  const publicLink = editingBudget?.public_token
                    ? `${window.location.origin}/proposal/${editingBudget.public_token}`
                    : null;
                  const total = (form.items || []).reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || 0), 0);
                  const firstName = (linkedClient.name || "").split(" ")[0] || "there";
                  const projectLabel = form.property_address || form.title || "your project";
                  const ctaButton = publicLink ? `<p><br></p><p><strong>👉 <a href="${publicLink}" target="_blank" style="color:#15803d;font-size:16px;">View &amp; Accept Your Proposal Online</a></strong></p><p style="color:#78716c;font-size:12px;">Or copy this link: ${publicLink}</p>` : `<p><em style="color:#92400e;">Save this proposal first to attach a public acceptance link.</em></p>`;
                  const body = `<p>Hi ${firstName},</p><p>Thank you for considering <strong>Georgia Outdoor Worx</strong> for ${projectLabel}. Please find your customized proposal at the link below.</p>${total > 0 ? `<p>Total estimated investment: <strong>${formatUSD(total)}</strong></p>` : ""}${ctaButton}<p><br></p><p>If you have any questions or would like to make adjustments, simply reply to this email or call us at <a href="tel:4784182629">478.418.2629</a>.</p><p><br></p><p>Best regards,</p><p><strong>${form.prepared_by_name || "Felipe Lima"}</strong><br>Georgia Outdoor Worx<br>${form.prepared_by_phone || "478.418.2629"} · contactus@georgiaoutdoorworx.com</p><p><em style="color:#a8a29e;font-size:11px;">Licensed &amp; insured · High-end craftsmanship for your lawn and garden</em></p>`;
                  onComposeEmail({ to: clientEmail, subject, body, source: "budget", clientId: linkedClient.id });
                  try {
                    const noteText = publicLink
                      ? `Email composition started — proposal link sent: ${form.tracking_id || ""}`
                      : `Email composition started — subject: "${subject}"`;
                    await api.post(`/admin/clients/${linkedClient.id}/notes`, { text: noteText });
                  } catch (e) { /* non-blocking */ }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-forest-300 text-forest-700 hover:bg-forest-50"
                title={`Compose email to ${clientEmail}`}
              >
                <Send className="w-4 h-4" /> Email Client
              </button>
            );
          })()}
          <button onClick={handleExportPDF} data-testid="export-pdf-btn" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50">
            <Download className="w-4 h-4" /> Export PDF
          </button>
          {editingBudget?.public_token && (
            <button
              type="button"
              data-testid="copy-public-link-btn"
              onClick={() => {
                const link = `${window.location.origin}/proposal/${editingBudget.public_token}`;
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(link).then(
                    () => toast.success("Public link copied! Paste it in an email or message."),
                    () => toast.error("Couldn't copy automatically. Link: " + link)
                  );
                } else {
                  toast.info(link);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-forest-300 text-forest-700 hover:bg-forest-50"
              title="Copy a public, no-login link the client can open to view & accept this proposal"
            >
              <FileText className="w-4 h-4" /> Copy Public Link
            </button>
          )}
          {!readOnly && (
            <button onClick={handleSave} data-testid="save-proposal-btn" className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
              <Check className="w-4 h-4" /> Save
            </button>
          )}
        </div>
      </div>

      {/* Status Buttons Row (only when editing existing) */}
      {editingBudget && (
        <div className="flex items-center justify-between flex-wrap gap-3 bg-white rounded-xl border border-stone-200 px-4 py-3 shadow-sm" data-testid="proposal-status-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500 mr-1">Status:</span>
            <button
              data-testid="editor-status-draft"
              disabled={readOnly || editingBudget.status === "draft"}
              onClick={() => handleStatusChange(editingBudget, "draft")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                editingBudget.status === "draft"
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"
              }`}
            >
              <Clock className="w-4 h-4 inline mr-1" /> Awaiting Submission
            </button>
            <button
              data-testid="editor-status-sent"
              disabled={readOnly || editingBudget.status === "sent"}
              onClick={() => handleStatusChange(editingBudget, "sent")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                editingBudget.status === "sent"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"
              }`}
            >
              <Send className="w-4 h-4 inline mr-1" /> Sent
            </button>
            <button
              data-testid="editor-status-approved"
              disabled={readOnly || editingBudget.status === "approved"}
              onClick={() => handleStatusChange(editingBudget, "approved")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                editingBudget.status === "approved"
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-green-700 border-green-200 hover:bg-green-50"
              }`}
            >
              <CheckCircle className="w-4 h-4 inline mr-1" /> Approved
            </button>
          </div>
          {editingBudget.status === "approved" && (
            <span className="text-xs text-green-700 bg-green-50 px-2.5 py-1 rounded-md border border-green-200">
              ✓ Service Order auto-created — see Service Orders module
            </span>
          )}
        </div>
      )}

      {/* Split Screen */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* LEFT: Form Inputs */}
        <div className="space-y-4 max-h-[calc(100vh-180px)] overflow-y-auto pr-1">
          {/* Header */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
            <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Header</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Client / Recipient Name *</label>
                <input data-testid="proposal-client-name" value={form.client_name_manual}
                  onChange={(e) => setForm(f => ({ ...f, client_name_manual: e.target.value, client_id: "" }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Type name or select below" required />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Link to Client <span className="text-stone-400 font-normal">(optional)</span></label>
                <select data-testid="proposal-client" value={form.client_id}
                  onChange={(e) => {
                    const cId = e.target.value;
                    const cName = clients.find(c => c.id === cId)?.name || "";
                    setForm(f => ({ ...f, client_id: cId, client_name_manual: cName || f.client_name_manual }));
                  }}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm">
                  <option value="">Not linked</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Tracking ID</label>
              <input data-testid="proposal-tracking" value={form.tracking_id || ""} onChange={(e) => setForm(f => ({ ...f, tracking_id: e.target.value }))}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Auto-generated" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Proposal Title *</label>
              <input data-testid="proposal-title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Landscaping Service Proposal" required />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Property Address</label>
              <input value={form.property_address} onChange={(e) => setForm(f => ({ ...f, property_address: e.target.value }))}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="123 Main St, City, GA" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Company Name <span className="text-stone-400 font-normal">(commercial only)</span></label>
              <input data-testid="proposal-company" value={form.company_name || ""} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Leave empty for residential" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Prepared By (Name)</label>
                <input data-testid="proposal-prepared-name" value={form.prepared_by_name || ""} onChange={(e) => setForm(f => ({ ...f, prepared_by_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Felipe Lima" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Prepared By (Phone)</label>
                <input data-testid="proposal-prepared-phone" value={form.prepared_by_phone || ""} onChange={(e) => setForm(f => ({ ...f, prepared_by_phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="478.418.2629" />
              </div>
            </div>
          </div>

          {/* Project Description */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
            <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Project Description</h3>
            <textarea value={form.project_description || ""} onChange={(e) => setForm(f => ({ ...f, project_description: e.target.value }))}
              rows={3} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none" placeholder="Overview of the project scope..." />
          </div>

          {/* Services Editor */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Service Details</h3>
              <button type="button" onClick={addService} className="text-forest-600 text-xs font-medium hover:text-forest-700 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Section</button>
            </div>
            {(form.proposal_services || []).map((svc, si) => (
              <div key={`svc-${si}`} className="border border-stone-200 rounded-lg p-3 space-y-2 bg-stone-50/50">
                <div className="flex items-center gap-2">
                  <input value={svc.title} onChange={(e) => updateServiceTitle(si, e.target.value)} placeholder="Service Title (e.g., Lawn Maintenance)"
                    className="flex-1 px-3 py-1.5 border border-stone-300 rounded-lg text-sm font-medium" />
                  {form.proposal_services.length > 1 && (
                    <button onClick={() => removeService(si)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  )}
                </div>
                {(svc.bullets || []).map((b, bi) => (
                  <div key={`b-${si}-${bi}`} className="flex items-center gap-2 pl-4">
                    <span className="text-stone-400 text-xs">•</span>
                    <input value={b} onChange={(e) => updateBullet(si, bi, e.target.value)} placeholder="Bullet point..."
                      className="flex-1 px-2 py-1 border border-stone-200 rounded text-sm" />
                    {svc.bullets.length > 1 && (
                      <button onClick={() => removeBullet(si, bi)} className="text-stone-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                    )}
                  </div>
                ))}
                <button onClick={() => addBullet(si)} className="text-xs text-forest-600 hover:text-forest-700 pl-4 flex items-center gap-1"><Plus className="w-3 h-3" /> Add bullet</button>
              </div>
            ))}
          </div>

          {/* Cost Items */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Cost Breakdown</h3>
              <button type="button" onClick={addItem} className="text-forest-600 text-xs font-medium hover:text-forest-700 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Item</button>
            </div>
            {form.items.map((item, idx) => (
              <div key={`item-${idx}`} className="flex gap-2 items-center">
                <input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Service"
                  className="flex-1 px-2 py-1.5 border border-stone-300 rounded text-sm" />
                <input type="number" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} min="1"
                  className="w-16 px-2 py-1.5 border border-stone-300 rounded text-sm text-center" />
                <CurrencyInput value={item.unit_price} onChange={(v) => updateItem(idx, "unit_price", v)}
                  className="w-28 px-2 py-1.5 border border-stone-300 rounded text-sm" data-testid={`budget-item-price-${idx}`} />
                {form.items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            <div className="text-right text-sm font-bold text-forest-700 pt-1 border-t border-stone-200">
              Total: {formatUSD(formTotal)}
            </div>
          </div>

          {/* Timeline, Payment, Schedule */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
            <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Timeline & Terms</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Timeline</label>
                <input value={form.timeline || ""} onChange={(e) => setForm(f => ({ ...f, timeline: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g., 2-3 business days" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Scheduled Date</label>
                <input type="date" value={form.scheduled_date || ""} onChange={(e) => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Payment Terms</label>
              <input value={form.payment_terms || ""} onChange={(e) => setForm(f => ({ ...f, payment_terms: e.target.value }))}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="To be discussed upon approval." />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Notes</label>
              <textarea value={form.notes || ""} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none" placeholder="Additional notes..." />
            </div>
          </div>
        </div>

        {/* RIGHT: Live Preview */}
        <div className="max-h-[calc(100vh-180px)] overflow-y-auto bg-stone-100 rounded-xl p-4 border border-stone-200" data-testid="proposal-preview-area">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wider flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Live Preview</span>
          </div>
          <div ref={previewRef}>
            <ProposalPreview form={form} clientName={clientName} />
          </div>
        </div>
      </div>

      {/* Modification History Timeline (only for saved proposals) */}
      {editingBudget && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm" data-testid="proposal-history">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-stone-500" />
            <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Modification History</h3>
            <span className="text-xs text-stone-400">
              ({(editingBudget.history_entries || []).length} {(editingBudget.history_entries || []).length === 1 ? "entry" : "entries"})
            </span>
          </div>
          {(editingBudget.history_entries || []).length === 0 ? (
            <p className="text-sm text-stone-400 italic py-2">No modifications recorded yet.</p>
          ) : (
            <ol className="relative border-l-2 border-stone-200 ml-2 space-y-3">
              {[...(editingBudget.history_entries || [])].reverse().map((h) => (
                <li key={h.id} className="ml-4" data-testid={`history-entry-${h.id}`}>
                  <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-forest-500 border-2 border-white shadow"></span>
                  <p className="text-sm text-stone-800">{h.action}</p>
                  <p className="text-[11px] text-stone-400 mt-0.5">
                    {formatHistoryDate(h.created_at)} · <span className="font-medium text-stone-500">{h.created_by}</span>
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
